/**
 * AtlasRentalsModule — RENTALS in the world (the interiors programme, slice R, 2026-10-01). "add rentals feature too":
 * a room an owner lets, a room a traveler takes. Rules lib/rentals.ts (≡ the server's), the claim and the let store
 * lib/rental_client.ts, the landlord's side in the structure console (modules/rentals/console_rentals.ts).
 *
 *   · THE THRESHOLD: inside a player structure, at a listed room's door, E offers the room ("E — rent room 101 · 40 ⓒ
 *     a day") or opens your own ("E — your room 101 · paid for 2 d 3 h"). ONE KEY, ONE FOCUS: the claim is made every
 *     frame through the arbiter (id `rental:<unit>`, the furniture's priority); a room let to someone else, or the
 *     landlord's own vacant room, is a passive line and takes no key.
 *   · THE ROOM WINDOW (`rentals`): the price, the days, the exact money (what goes to the landlord today, what waits in
 *     escrow), Rent; for the tenant: the clock, Pay, Leave (two clicks), the lease's receipts. SOFT RENDER.
 *   · THE PLATE: under the visit's own number plate (the visit is slice G's file — untouched), a second small plate
 *     with the tenant's name or "TO LET · 40" — `signTexture(…, { kind: 'plate' })`, unlit like the room.
 *   · THE LET STORE: which rooms are let, for `deed_home` (no versona resident is bedded in a tenant's room).
 *   · TOWN → "My rooms": your leases (pay, leave, waypoint) and the rooms you let.
 *
 * Everything here mirrors rows; every act is a reducer call and every refusal is the server's own words in a toast.
 * Lever: `__rentals()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyIs } from '../lib/keybinds.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';
import { enterables, interiorSpec } from '../lib/building_interiors.js';
import { floorYOf, activeInterior } from '../lib/interior_wiring.js';
import { shellFixtures, kitProp } from '../lib/interior_kit_props.js';
import type { PhysicsModule } from './PhysicsModule.js';
import { interactFocus } from '../lib/interact_focus.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import { signTexture } from './interior/interior_look.js';
import { el, btn, select, head, muted, card, act, debounced } from './town/town_ui.js';
import { registerTownTab } from './town/town_tabs.js';
import { RENT_HORIZON_DAYS, SHELL_FLOOR_H, shellFloors, floorOfHeight, rentQuote, maxExtraDays } from '../lib/rentals.js';
import {
  setLetUnits, letStoreVersion, unitNumber, unitKeys, leaseStatus, leaseFacts, plateText, thresholdLabel,
  type LetUnit, type RentalUnitRow, type RentalLeaseRow,
} from '../lib/rental_client.js';
import {
  rentalDb, rentalReducers, me, allUnits, allLeases, allReceipts, unitsOfStructure, liveLeaseOfUnit, isMine, structureRow, structureName,
} from './rentals/rental_rows.js';

/** How near a room's threshold (its standing point or its door point) the offer is made (m). */
export const RENT_REACH_M = 2.0;
/** How far under the visit's number plate the tenant's plate hangs (m, centre to centre). */
const PLATE_DROP = 0.16;

interface Near { unit: RentalUnitRow; lease: RentalLeaseRow | null; dist: number; label: string; act: boolean }

export class AtlasRentalsModule implements IModule {
  readonly name = 'AtlasRentals';

  private app!: App;
  private scene!: THREE.Scene;
  private prompt: HTMLElement | null = null;
  private near: Near | null = null;
  /** THE THRESHOLD SCAN'S CADENCE (slice H, 2026-10-02): `findNear` walks the structure's units and their leases; it ran
   *  every frame. It runs every `SCAN_EVERY` frames now (and at once when the rows change); between scans the nearest
   *  unit's distance is re-measured every frame (the claim is per frame — law 43) and a step out of reach drops it at
   *  once. `localStorage atlas:rentals-scan = 'frame'` = every frame, as before. */
  private frameN = 0;
  private readonly scanStats = { frames: 0, scans: 0, ms: 0, refreshes: 0 };
  private scanEveryAt = -1e9; private scanEveryN = 10;
  private scanEvery(): number {
    const now = performance.now();
    if (now - this.scanEveryAt > 1000) { this.scanEveryAt = now; try { this.scanEveryN = localStorage.getItem('atlas:rentals-scan') === 'frame' ? 1 : 10; } catch { this.scanEveryN = 10; } }
    return this.scanEveryN;
  }
  private win: UiWindow | null = null;
  private winFor: { structureId: bigint; unitId: string } | null = null;
  private winAt = 0;
  private leaveArmedAt = 0;
  private subscribed = false;
  private dirty = true;
  private storeSig = '';
  private storeAt = 0;
  private plates: THREE.Group | null = null;
  private plateSig = '';
  private plateAt = 0;
  private numberPlates = 0;
  private plateList: Array<{ unitId: string; text: string; x: number; y: number; z: number; nx: number; nz: number; floor: number }> = [];
  private tabEl: HTMLElement | null = null;
  private onKey = (e: KeyboardEvent): void => this.key(e);
  private readonly rerender = debounced(() => {
    if (this.win?.isOpen()) this.render();
    if (this.tabEl?.isConnected) this.renderTab(this.tabEl);
  }, 150);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    const p = document.createElement('div');
    p.id = 'rental-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg2) 86%, transparent);border:1px solid var(--ax-line2);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    window.addEventListener('keydown', this.onKey);
    bus.on('atlas:place-requested', () => this.press()); // (slice H) the pad's interact reaches the room's door window
    app.world.addSystem(makeSystem('AtlasRentals', Phase.UI, 38, () => this.tick()));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => { this.win?.setOpen(false); this.clearPlates(); this.near = null; });
    registerTownTab({ key: 'rooms', label: 'My rooms', order: 6, render: (h) => { this.tabEl = h; this.renderTab(h); } });
    (window as unknown as { __rentals?: unknown }).__rentals = () => this.diag();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.prompt?.remove();
    this.clearPlates();
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM rental_unit', 'SELECT * FROM rental_lease',
        'SELECT * FROM rental_container', 'SELECT * FROM rental_stored', 'SELECT * FROM rental_receipt', // row-level secured: only my own rows arrive
      ]);
    } catch { /* pre-publish */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const name of ['rentalUnit', 'rentalLease', 'rentalReceipt', 'structureMeta']) {
      const t = db[name];
      const ping = (): void => { this.dirty = true; this.rerender(); };
      t?.onInsert?.(ping); t?.onUpdate?.(ping); t?.onDelete?.(ping);
    }
  }

  // ─── The frame ──────────────────────────────────────────────────────────────

  private tick(): void {
    const now = performance.now();
    if (this.dirty || now - this.storeAt > 2000) { this.storeAt = now; this.syncStore(); }
    this.scanStats.frames++;
    if (this.dirty || ++this.frameN % this.scanEvery() === 0) { const t0 = performance.now(); this.findNear(); this.scanStats.scans++; this.scanStats.ms += performance.now() - t0; }
    else this.refreshNear();
    const focus = interactFocus();
    const n = this.near;
    if (n?.act) focus.claim({ id: `rental:${n.unit.unitId}`, dist: n.dist, priority: 2, label: n.label });
    // an acting claim shows when it owns the key; a passive line only when nobody asks for the key at all
    const show = !!n && !this.win?.isOpen() && (n.act ? focus.owns(`rental:${n.unit.unitId}`) : focus.focus() === null);
    if (this.prompt) {
      const cur = this.prompt.style.display !== 'none';
      if (show !== cur) this.prompt.style.display = show ? 'block' : 'none';
      if (show && n && this.prompt.textContent !== n.label) this.prompt.textContent = n.label;
    }
    if (now - this.plateAt > 500) { this.plateAt = now; this.syncPlates(); }
    if (this.win?.isOpen() && now - this.winAt > 5000) this.render(); // the clock lines
    this.dirty = false;
  }

  /** The let store (for `deed_home`): rebuilt when the live leases change. */
  private syncStore(): void {
    const leases = allLeases().filter((l) => l.state === 'live');
    const sig = leases.map((l) => `${l.leaseId}:${l.unitRowId}`).sort().join(',');
    if (sig === this.storeSig) return;
    this.storeSig = sig;
    const units = new Map(allUnits().map((u) => [u.unitRowId, u]));
    const out = new Map<string, LetUnit[]>();
    for (const l of leases) {
      const u = units.get(l.unitRowId);
      if (!u) continue;
      const id = `deed:${l.structureId}`;
      const list = out.get(id) ?? [];
      list.push({ unitId: u.unitId, floor: u.floor, x0: u.x0, z0: u.z0, x1: u.x1, z1: u.z1, leaseId: l.leaseId, tenantHex: l.tenant.toHexString(), tenantName: l.tenantName });
      out.set(id, list);
    }
    setLetUnits(out);
  }

  /** The player in the active deed interior's own frame (null: not in one). */
  private localPose(): { structureId: bigint; lx: number; lz: number; floor: number } | null {
    const interior = atlas.interiorId;
    if (!interior || !interior.startsWith('deed:')) return null;
    const b = enterables.get(interior);
    if (!b) return null;
    const c = Math.cos(b.yaw), s = Math.sin(b.yaw), dx = atlas.localX - b.x, dz = atlas.localZ - b.z;
    return { structureId: BigInt(interior.slice(5)), lx: dx * c - dz * s, lz: dx * s + dz * c, floor: floorOfHeight(atlas.localY - b.groundY, shellFloors(b.h)) };
  }

  /** Between scans: the cached unit's distance from where the player stands NOW (cheap); out of reach, or off its
   *  floor, or out of the interior — gone at once (the next scan finds whatever else is near). */
  private refreshNear(): void {
    const n = this.near;
    if (!n) return;
    const pose = atlas.seated ? null : this.localPose();
    if (!pose || pose.structureId !== n.unit.structureId || pose.floor !== n.unit.floor) { this.near = null; return; }
    const u = n.unit;
    n.dist = Math.min(Math.hypot(pose.lx - u.ax, pose.lz - u.az), Math.hypot(pose.lx - u.tx, pose.lz - u.tz));
    if (n.dist > RENT_REACH_M) this.near = null;
    this.scanStats.refreshes++;
  }

  private findNear(): void {
    this.near = null;
    const pose = atlas.seated ? null : this.localPose();
    const who = me();
    if (!pose || !who) return;
    const s = structureRow(pose.structureId);
    let best: Near | null = null;
    for (const u of unitsOfStructure(pose.structureId)) {
      if (u.floor !== pose.floor) continue;
      const d = Math.min(Math.hypot(pose.lx - u.ax, pose.lz - u.az), Math.hypot(pose.lx - u.tx, pose.lz - u.tz));
      if (d > RENT_REACH_M || (best && d >= best.dist)) continue;
      const lease = liveLeaseOfUnit(u.unitRowId);
      const role = lease && isMine(lease) ? 'tenant' : s && s.owner.isEqual(who) ? 'owner' : 'visitor';
      const t = thresholdLabel(u, lease, role, lease ? leaseStatus(leaseFacts(lease), Date.now() / 1000).line : '');
      if (t.label) best = { unit: u, lease, dist: d, label: t.label, act: t.act };
    }
    this.near = best;
  }

  private key(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.repeat || !keyIs(e.code, 'interact')) return; // the BOUND key: a rebound E still opened it
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    this.press();
  }
  /** (slice H — item 5) The E key and the interact ACTION (a pad's button, a rebound key) — one press however it came. */
  private lastPress = 0;
  private press(): void {
    const now = performance.now();
    if (now - this.lastPress < 150) return;
    this.lastPress = now;
    const n = this.near;
    if (!n?.act || !interactFocus().owns(`rental:${n.unit.unitId}`)) return;
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    this.open(n.unit.structureId, n.unit.unitId);
  }

  // ─── The plate ──────────────────────────────────────────────────────────────

  private clearPlates(): void {
    if (this.plates) { deferDisposeTree(this.plates); this.plates = null; }
    this.plateSig = ''; this.plateList = [];
  }

  /**
   * THE PLATE. Where slice F's fixture rule hangs a room's number plate (`shellFixtures` → `door_plate`, `of` = the
   * unit), a second small plate hangs just under it: the tenant's name, or "TO LET · <rent>". The visit draws the
   * number plate only at interior detail ≥ 1 — where it does not, this plate carries the number too ("101 · TO LET ·
   * 12"). Built in the visit's own fixture frame (a group at the building, turned by its yaw), unlit like the room;
   * rebuilt when the rows, the room or the visit's plates change.
   */
  private syncPlates(): void {
    const pose = this.localPose();
    const interior = atlas.interiorId;
    const act = activeInterior();
    const d = pose && interior && act && act.id === interior ? act.descriptor : null;
    const b = interior ? enterables.get(interior) : undefined;
    if (!pose || !interior || !d || !b) { if (this.plates) this.clearPlates(); return; }
    const units = unitsOfStructure(pose.structureId);
    let numbered = 0;
    this.scene.traverse((o) => { if (o.name === 'interior-plate') numbered++; });
    this.numberPlates = numbered;
    const texts = units.map((u) => `${u.unitId}=${plateText(u, liveLeaseOfUnit(u.unitRowId))}`).join('|');
    const sig = `${interior}|${act!.version}|${numbered > 0 ? 1 : 0}|${texts}`;
    if (sig === this.plateSig) return;
    this.clearPlates();
    this.plateSig = sig;
    const ps = kitProp('door_plate');
    if (units.length === 0 || !ps) return;
    const root = new THREE.Group();
    root.name = 'rental-plates';
    root.position.set(b.x, b.groundY, b.z); // a scene child is ABSOLUTE; the plates are building-local under it (the visit's fixture frame)
    root.rotation.y = b.yaw;
    const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
    for (const fx of shellFixtures(interiorSpec(b.w, b.h, b.d, b.key), d, {})) {
      if (fx.key !== 'door_plate' || !fx.of) continue;
      const unit = units.find((u) => u.unitId === fx.of);
      if (!unit) continue;
      const status = plateText(unit, liveLeaseOfUnit(unit.unitRowId));
      if (!status) continue;
      const text = numbered > 0 ? status : `${unitNumber(unit.unitId)} · ${status}`;
      const tex = signTexture(text, undefined, { kind: 'plate' });
      const mat = new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }); // unlit, like the room and the visit's own plate
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(ps.w * 0.86, ps.h * 0.8), mat);
      const fy = fx.floor === 0 ? 0 : fx.floor * SHELL_FLOOR_H; // slice F's convention: a fixture's y is from the storey's nominal level
      const lx = fx.x + Math.sin(fx.yaw) * (ps.d / 2 + 0.006), lz = fx.z + Math.cos(fx.yaw) * (ps.d / 2 + 0.006);
      const ly = fy + fx.y + ps.h / 2 - (numbered > 0 ? PLATE_DROP : 0);
      mesh.position.set(lx, ly, lz);
      mesh.rotation.y = fx.yaw;
      mesh.name = 'rental-plate';
      mesh.userData.text = text; mesh.userData.unitId = unit.unitId;
      root.add(mesh);
      const nlx = Math.sin(fx.yaw), nlz = Math.cos(fx.yaw); // the plate's face normal, building-local → world
      this.plateList.push({ unitId: unit.unitId, text, x: b.x + lx * c + lz * s, y: b.groundY + ly, z: b.z - lx * s + lz * c, nx: nlx * c + nlz * s, nz: -nlx * s + nlz * c, floor: fx.floor });
    }
    this.scene.add(root);
    this.plates = root;
  }

  // ─── The room window ────────────────────────────────────────────────────────

  private open(structureId: bigint, unitId: string): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'rentals', title: 'Room', icon: 'build', width: 480, height: 430, minWidth: 380, minHeight: 280 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;font:13px var(--ax-font);color:var(--ax-text);overflow:auto;padding:10px 12px;gap:10px;';
    }
    this.winFor = { structureId, unitId };
    this.leaveArmedAt = 0;
    this.win.setOpen(true);
    this.render();
  }

  private render(): void {
    const win = this.win, f = this.winFor;
    if (!win || !f) return;
    this.winAt = performance.now();
    softRender(win.body, (body) => this.build(win, body, f.structureId, f.unitId));
  }

  private build(win: UiWindow, body: HTMLElement, structureId: bigint, unitId: string): void {
    const u = unitsOfStructure(structureId).find((x) => x.unitId === unitId) ?? null;
    const num = unitNumber(unitId), place = structureName(structureId);
    win.setTitle(`Room ${num} · ${place}`);
    const r = rentalReducers();
    const mine = allLeases().filter((l) => l.structureId === structureId && l.unitId === unitId && isMine(l)).sort((a, b) => (a.leaseId < b.leaseId ? 1 : -1))[0] ?? null;
    const lease = u ? liveLeaseOfUnit(u.unitRowId) : null;
    const nowS = Date.now() / 1000;
    body.appendChild(head(`ROOM ${num} — ${place.toUpperCase()}`));
    if (u?.notes) body.appendChild(el('div', 'color:var(--ax-soft)', `"${u.notes}"`));

    if (lease && isMine(lease)) {
      const st = leaseStatus(leaseFacts(lease), nowS);
      const c = card();
      const line = el('div', `color:${st.phase === 'paid' ? 'var(--ax-good)' : 'var(--ax-warn)'};font-weight:600`, `Your room — ${st.line}`);
      line.setAttribute('data-k', 'lease:status');
      c.appendChild(line);
      c.appendChild(muted(`Signed at ${lease.rentPerDay} ⓒ a day · ${lease.paidDays} day${lease.paidDays === 1 ? '' : 's'} paid · leaving now returns ${st.refund} ⓒ from escrow (the day in progress is the landlord's).`));
      const max = maxExtraDays({ sinceS: leaseFacts(lease).sinceS, paidDays: lease.paidDays, releasedDays: lease.releasedDays, rentPerDay: lease.rentPerDay, noticeEndS: leaseFacts(lease).endsAtS }, nowS);
      if (max > 0) {
        const pick = select(Array.from({ length: Math.min(max, RENT_HORIZON_DAYS) }, (_, i) => ({ value: String(i + 1), label: `${i + 1} day${i ? 's' : ''} · ${(i + 1) * lease.rentPerDay} ⓒ` })), '1');
        pick.setAttribute('data-k', 'pay:days');
        const pay = btn('Pay rent', () => {
          const days = Math.max(1, Number((fieldByKey<HTMLSelectElement>(win.body, 'pay:days') ?? pick).value) || 1);
          void act(r?.payRent({ leaseId: lease.leaseId, days }), `Paid ${days} more day${days === 1 ? '' : 's'} for room ${num}.`);
        }, true);
        pay.setAttribute('data-k', 'pay:go');
        const row = el('div', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap'); row.append(pick, pay);
        c.appendChild(row);
      } else c.appendChild(muted(lease.noticeAt.microsSinceUnixEpoch !== 0n ? 'The landlord gave notice — no more days can be bought.' : `You are paid up ${RENT_HORIZON_DAYS} days ahead — as far as a lease goes.`));
      const armed = Date.now() - this.leaveArmedAt < 6000;
      const leave = btn(armed ? `Confirm — leave room ${num} (${st.refund} ⓒ and your stored goods come back)` : 'Leave the room…', () => {
        if (Date.now() - this.leaveArmedAt < 6000) { this.leaveArmedAt = 0; void act(r?.leaveRental({ leaseId: lease.leaseId }), `You left room ${num}. What you stored is back in your inventory.`); }
        else { this.leaveArmedAt = Date.now(); this.render(); window.setTimeout(() => this.render(), 6100); }
      });
      leave.setAttribute('data-k', 'lease:leave');
      c.appendChild(leave);
      c.appendChild(muted('The room\'s storage and bed are yours alone while the lease stands: press E at the wardrobe, the chest or the bed. Past the paid days you have 24 h of grace; after it the room is released and what you stored returns to your inventory.'));
      body.appendChild(c);
    } else if (lease) {
      body.appendChild(el('div', 'color:var(--ax-soft)', `Let to ${lease.tenantName}.`));
    } else if (u?.listed) {
      const who = me(), s = structureRow(structureId);
      if (who && s && s.owner.isEqual(who)) body.appendChild(muted(`Your room — to let at ${u.rentPerDay} ⓒ a day. Manage it at the structure console.`));
      else {
        const c = card();
        c.appendChild(el('div', 'font-weight:600', `${u.rentPerDay} ⓒ a day`));
        const pick = select(Array.from({ length: RENT_HORIZON_DAYS }, (_, i) => ({ value: String(i + 1), label: `${i + 1} day${i ? 's' : ''}` })), '3');
        pick.setAttribute('data-k', 'rent:days');
        pick.addEventListener('change', () => this.render());
        const days = Math.max(1, Math.min(RENT_HORIZON_DAYS, Number((fieldByKey<HTMLSelectElement>(win.body, 'rent:days') ?? pick).value) || 3));
        const q = rentQuote(u.rentPerDay, days);
        const quote = muted(`${days} day${days === 1 ? '' : 's'}: ${q.total} ⓒ — ${q.first.toOwner + q.first.fee} for today${q.first.fee > 0 ? ` (${q.first.fee} of it the exchange fee)` : ''}${q.escrow > 0 ? `, ${q.escrow} held in escrow and paid to the landlord a day at a time. Days that have not begun come back if you leave.` : '.'}`);
        quote.setAttribute('data-k', 'rent:quote');
        const go = btn('Rent this room', () => {
          const d = Math.max(1, Number((fieldByKey<HTMLSelectElement>(win.body, 'rent:days') ?? pick).value) || 3);
          void act(r?.rentUnit({ structureId, unitId, days: d, expectRentPerDay: u.rentPerDay }), `Room ${num} is yours — paid for ${d} day${d === 1 ? '' : 's'}.`);
        }, true);
        go.setAttribute('data-k', 'rent:go');
        const row = el('div', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap'); row.append(pick, go);
        c.append(row, quote);
        c.appendChild(muted('The room comes with its furniture: its storage and its bed are yours alone while the lease stands, and the landlord cannot redeed, refit or re-pack the building under you. Rent runs out → 24 h of grace → the room is released and your goods return to your inventory.'));
        body.appendChild(c);
      }
    } else body.appendChild(muted('This room is not to let.'));

    if (mine) {
      const rec = allReceipts().filter((x) => x.leaseId === mine.leaseId).sort((a, b) => (a.receiptId < b.receiptId ? 1 : -1)).slice(0, 6);
      if (rec.length) body.appendChild(head('RECEIPTS'));
      for (const x of rec) {
        const line = muted(`${x.kind}${x.amountSov > 0n ? ` · ${x.amountSov} ⓒ` : ''} — ${x.detail}`);
        line.setAttribute('data-row', `receipt:${x.receiptId}`);
        body.appendChild(line);
      }
    }
  }

  // ─── Town → My rooms ────────────────────────────────────────────────────────

  private renderTab(real: HTMLElement): void { softRender(real, (h) => this.buildTab(h)); }

  private buildTab(h: HTMLElement): void {
    const who = me(), r = rentalReducers(), nowS = Date.now() / 1000;
    h.style.cssText += 'display:flex;flex-direction:column;gap:8px;';
    h.appendChild(head('MY ROOMS'));
    const leases = allLeases();
    const mineLive = leases.filter((l) => isMine(l) && l.state === 'live'), mineEnded = leases.filter((l) => isMine(l) && l.state !== 'live').sort((a, b) => (a.leaseId < b.leaseId ? 1 : -1)).slice(0, 4);
    if (mineLive.length === 0) h.appendChild(muted('You rent no room. Rooms are let inside player buildings — a hotel\'s guest rooms, a large house\'s spare room: walk to a room\'s door plate and press E.'));
    for (const l of mineLive) {
      const st = leaseStatus(leaseFacts(l), nowS), num = unitNumber(l.unitId);
      const c = card();
      c.setAttribute('data-row', `lease:${l.leaseId}`);
      c.appendChild(el('div', 'font-weight:600', `Room ${num} · ${structureName(l.structureId)}`));
      const line = el('div', `color:${st.phase === 'paid' ? 'var(--ax-good)' : 'var(--ax-warn)'}`, `${st.line} · ${l.rentPerDay} ⓒ a day`);
      line.setAttribute('data-k', `lease:status:${l.leaseId}`);
      c.appendChild(line);
      const row = el('div', 'display:flex;gap:8px;flex-wrap:wrap');
      const pay = btn(`Pay 1 day (${l.rentPerDay} ⓒ)`, () => void act(r?.payRent({ leaseId: l.leaseId, days: 1 }), `Paid a day for room ${num}.`), true);
      pay.setAttribute('data-k', `lease:pay:${l.leaseId}`);
      row.appendChild(pay);
      const s = structureRow(l.structureId);
      if (s && r?.createWaypoint) row.appendChild(btn('Waypoint', () => void act(r.createWaypoint!({ name: `Room ${num}: ${structureName(l.structureId)}`.slice(0, 32), posX: s.posX, posZ: s.posZ }), 'Waypoint set on your room\'s building')));
      const leave = btn('Manage / leave…', () => this.open(l.structureId, l.unitId));
      leave.setAttribute('data-k', `lease:manage:${l.leaseId}`);
      row.appendChild(leave);
      c.appendChild(row);
      h.appendChild(c);
    }
    for (const l of mineEnded) {
      const line = muted(`Room ${unitNumber(l.unitId)} · ${structureName(l.structureId)} — ${leaseStatus(leaseFacts(l), nowS).line}`);
      line.setAttribute('data-row', `ended:${l.leaseId}`);
      h.appendChild(line);
    }
    // the rooms you let
    const mineUnits = allUnits().filter((u) => { const s = structureRow(u.structureId); return !!who && !!s && s.owner.isEqual(who); });
    if (mineUnits.length) {
      h.appendChild(head('ROOMS YOU LET'));
      for (const u of mineUnits) {
        const l = liveLeaseOfUnit(u.unitRowId);
        const line = el('div', `color:${l ? 'var(--ax-good)' : 'var(--ax-soft)'}`, `Room ${unitNumber(u.unitId)} · ${structureName(u.structureId)} — ${l ? `let to ${l.tenantName} at ${l.rentPerDay} ⓒ · ${leaseStatus(leaseFacts(l), nowS).line}` : u.listed ? `to let at ${u.rentPerDay} ⓒ a day` : 'not listed'}`);
        line.setAttribute('data-row', `unit:${u.unitRowId}`);
        h.appendChild(line);
      }
      h.appendChild(muted('List, price and give notice at the structure console inside the building.'));
    }
  }

  // ─── Lever ──────────────────────────────────────────────────────────────────

  private diag(): unknown {
    const pose = this.localPose();
    return {
      interior: atlas.interiorId,
      pose,
      units: pose ? unitsOfStructure(pose.structureId).map((u) => ({ unitId: u.unitId, number: unitNumber(u.unitId), listed: u.listed, rent: u.rentPerDay, floor: u.floor, door: [u.tx, u.tz], stand: [u.ax, u.az], keys: unitKeys(u), tenant: liveLeaseOfUnit(u.unitRowId)?.tenantName ?? null })) : [],
      near: this.near ? { unitId: this.near.unit.unitId, dist: this.near.dist, label: this.near.label, act: this.near.act } : null,
      promptShown: !!this.prompt && this.prompt.style.display !== 'none',
      scan: { every: this.scanEvery(), ...this.scanStats, ms: Math.round(this.scanStats.ms * 100) / 100, msPerScan: this.scanStats.scans ? Math.round(this.scanStats.ms / this.scanStats.scans * 1000) / 1000 : 0 },
      promptText: this.prompt?.textContent ?? '',
      windowOpen: !!this.win?.isOpen(),
      windowText: this.win?.isOpen() ? (this.win.body.textContent ?? '').slice(0, 600) : '',
      plates: this.plateList,
      numberPlates: this.numberPlates,
      letStore: letStoreVersion(),
      myLeases: allLeases().filter((l) => isMine(l)).map((l) => ({ leaseId: l.leaseId.toString(), unitId: l.unitId, state: l.state, paidDays: l.paidDays })),
      tables: { units: allUnits().length, leases: allLeases().length, receipts: allReceipts().length, hasDb: !!rentalDb().rentalUnit },
      /** Where a unit's threshold standing point is in the world (a probe stands there). */
      thresholdWorld: (unitId: string): { x: number; z: number; y: number } | null => {
        const interior = atlas.interiorId, b = interior ? enterables.get(interior) : undefined;
        const u = pose ? unitsOfStructure(pose.structureId).find((x) => x.unitId === unitId) : undefined;
        if (!b || !u) return null;
        const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
        return { x: b.x + u.ax * c + u.az * s, z: b.z - u.ax * s + u.az * c, y: b.groundY + floorYOf(u.floor) };
      },
      /** Stand at a unit's threshold (a probe; the real game walks the stairs). The SERVER row moves too. */
      standAt: async (unitId: string): Promise<boolean> => {
        const interior = atlas.interiorId, b = interior ? enterables.get(interior) : undefined;
        const u = pose ? unitsOfStructure(pose.structureId).find((x) => x.unitId === unitId) : undefined;
        if (!b || !u) return false;
        const c = Math.cos(b.yaw), s = Math.sin(b.yaw);
        const x = b.x + u.ax * c + u.az * s, z = b.z - u.ax * s + u.az * c, y = b.groundY + floorYOf(u.floor);
        try { await (atlas.conn!.reducers as unknown as { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }).debugTeleport({ posX: x, posZ: z }); } catch { /* no dev flag: the local hop alone */ }
        // The row's own update may land a frame or two later and the reconcile then re-seats the capsule on the row's
        // XZ at ground level — the lever holds the storey until the capsule stays (a probe's race, not the game's).
        const phys = this.app.getModule<PhysicsModule>('PhysicsModule');
        for (let i = 0; i < 4; i++) {
          phys?.teleportAvatarY(x, y + 0.05, z);
          await new Promise((r) => window.setTimeout(r, 450));
          if (this.localPose()?.floor === u.floor && Math.hypot(atlas.localX - x, atlas.localZ - z) < 0.6) break;
        }
        return this.localPose()?.floor === u.floor;
      },
      /** Stand `back` metres before a room's plate, facing it (a probe's picture; the SERVER row moves too). */
      lookAtPlate: async (unitId: string, back = 1.3): Promise<boolean> => {
        const interior = atlas.interiorId, b = interior ? enterables.get(interior) : undefined;
        const p = this.plateList.find((q) => q.unitId === unitId);
        if (!b || !p) return false;
        const x = p.x + p.nx * back, z = p.z + p.nz * back, y = b.groundY + floorYOf(p.floor);
        try { await (atlas.conn!.reducers as unknown as { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }).debugTeleport({ posX: x, posZ: z }); } catch { /* no dev flag */ }
        const phys = this.app.getModule<PhysicsModule>('PhysicsModule');
        for (let i = 0; i < 4; i++) {
          phys?.teleportAvatarY(x, y + 0.05, z);
          await new Promise((r) => window.setTimeout(r, 450));
          if (this.localPose()?.floor === p.floor && Math.hypot(atlas.localX - x, atlas.localZ - z) < 0.6) break;
        }
        const yaw = Math.atan2(-(p.x - atlas.localX), -(p.z - atlas.localZ));
        atlas.localYaw = yaw;
        if (atlas.localMesh) atlas.localMesh.rotation.y = yaw;
        (window as unknown as { __cameraLook?: (pitch: number) => number }).__cameraLook?.(Math.atan2(p.y - (atlas.localY + 1.6), Math.hypot(p.x - atlas.localX, p.z - atlas.localZ)));
        return true;
      },
      open: (unitId: string) => { if (pose) this.open(pose.structureId, unitId); },
      close: () => this.win?.setOpen(false),
      rent: (unitId: string, days: number) => { const u = pose ? unitsOfStructure(pose.structureId).find((x) => x.unitId === unitId) : undefined; return u ? rentalReducers()!.rentUnit({ structureId: u.structureId, unitId, days, expectRentPerDay: u.rentPerDay }) : Promise.reject(new Error('no such unit here')); },
    };
  }
}
